import { Injectable } from '@angular/core';
import {
  ActivatedRoute,
  ActivationStart,
  NavigationEnd,
  Router,
} from '@angular/router';
import {
  Action,
  NgxsOnInit,
  Selector,
  SelectorOptions,
  State,
  StateContext,
} from '@ngxs/store';
import {
  ACCESS_ID_PARAM,
  AppContext,
  REFRESH_PAGE_ROUTE,
} from '@osos/shared/types';
import { filter, tap } from 'rxjs/operators';
import { NavigationStateActions } from '../navigation/navigation.actions';
import { NotificationActions } from '../notification/notification.action';
import { AppStateActions } from './app.actions';

export interface AppStateModel {
  currentAppContext: AppContext;
  accessId: string;
  implicitAccessId: string;
  headlessMode: {
    enabled: boolean;
    config?: {
      language: string;
      timeZoneOffset: number;
      userId: string;
    };
  };
  defaultLanguage: string;
}
@State<AppStateModel>({
  name: 'app',
  defaults: {
    currentAppContext: AppContext.Public,
    accessId: undefined,
    implicitAccessId: undefined,
    headlessMode: {
      enabled: false,
    },
    defaultLanguage: '',
  },
})
@Injectable()
@SelectorOptions({ injectContainerState: false })
export class AppState implements NgxsOnInit {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  ngxsOnInit({ getState, patchState, dispatch }: StateContext<AppStateModel>) {
    this.router.events
      .pipe(
        tap((event) => {
          // Handle App Context change:
          if (
            event instanceof ActivationStart &&
            event.snapshot.data['appContext'] !== undefined
          ) {
            const { appContext } = event.snapshot.data;
            const { currentAppContext } = getState();

            if (appContext !== currentAppContext) {
              patchState({ currentAppContext: appContext });
              dispatch(new AppStateActions.AppContextChanged(appContext));
            }
          }
        }),
        filter(
          (event) =>
            event instanceof NavigationEnd &&
            getState().currentAppContext === AppContext.Public &&
            event.url !== `/${REFRESH_PAGE_ROUTE}`,
        ),
      )
      .subscribe(() => {
        // Check if the implicitAccessId is detected.
        let accessId: string = getState().implicitAccessId;

        if (!accessId) {
          let route = this.route.root.snapshot;

          while (route.firstChild) {
            route = route.firstChild;

            if (route?.params[ACCESS_ID_PARAM]) {
              accessId = route?.params[ACCESS_ID_PARAM];
              break;
            }
          }
        }

        patchState({
          accessId,
          // Clear the implicitAccessId to be able to check if it's changed later or not.
          implicitAccessId: undefined,
        });
      });
  }

  @Selector()
  static currentAppContext(state: AppStateModel) {
    return state.currentAppContext;
  }

  @Selector()
  static accessId(state: AppStateModel) {
    return state.implicitAccessId ?? state.accessId;
  }

  @Selector()
  static headlessModeEnabled(state: AppStateModel) {
    return state.headlessMode?.enabled;
  }
  @Selector()
  static defaultLanguage(state: AppStateModel) {
    return state?.defaultLanguage;
  }

  @Selector()
  static headlessConfig(state: AppStateModel) {
    return state?.headlessMode?.enabled ? state.headlessMode?.config : null;
  }

  @Action(AppStateActions.LoadAnonymousRelatedData)
  loadAnonymousRelatedData({ dispatch }: StateContext<AppStateModel>) {
    dispatch(new NavigationStateActions.FetchItemsPinnedToSidebarV2());
  }

  @Action(AppStateActions.LoadUserRelatedData)
  loadUserRelatedData({ dispatch, getState }: StateContext<AppStateModel>) {
    const { enabled } = getState().headlessMode;
    dispatch(new NavigationStateActions.FetchItemsPinnedToSidebarV2());
    if (!enabled) {
      // Trigger the interval of fetching user notification
      dispatch(new NotificationActions.ScheduleNotificationFetching());
      setTimeout(() => {
        dispatch(new NavigationStateActions.FetchSidebarStatusCounts());
      }, 1000);
    }
  }

  @Action(AppStateActions.SetAccessId)
  setAccessId(
    { patchState }: StateContext<AppStateModel>,
    { accessId }: AppStateActions.SetAccessId,
  ) {
    patchState({ implicitAccessId: accessId });
  }

  @Action(AppStateActions.SetHeadlessModeConfig)
  setHeadlessModeConfig(
    { patchState }: StateContext<AppStateModel>,
    { enabled, config }: AppStateActions.SetHeadlessModeConfig,
  ) {
    patchState({
      headlessMode: {
        enabled,
        config,
      },
    });
  }

  @Action(AppStateActions.SetDefaultLanguageConfig)
  setDefaultLanguageConfig(
    { patchState }: StateContext<AppStateModel>,
    { language }: AppStateActions.SetDefaultLanguageConfig,
  ) {
    patchState({
      defaultLanguage: language,
    });
  }
}
